import { EV } from '../core/eventbus.js';

/**
 * 电视机 / 游戏桌：内嵌一块网页，房间里所有人看同一个画面。
 * 同步思路：只同步「网址 + 开播时刻」，各自按 (现在 - 开播) 算出该跳到几秒，
 * 这样不用拿到对方播放器权限，也能大致同步。
 *
 * 电视与游戏桌是**两块互不关联的小窗**：各有一份面板、各记各的网址、
 * 各开各的小窗与全屏，谁换台都不影响另一边。同步消息里带 kind（哪件家具），
 * 收到不是自己这件的消息就当没听见，所以看电视的不会影响打牌的。
 */

/** 能开这种面板的器物：电视与游戏桌，各起一个实例，互不打扰 */
export const STAGE_TYPES = ['tv', 'gametable'];

/** 器物 -> 面板上的叫法与提示 */
const STAGE_TEXT = {
  tv: { title: '电视机', bar: '电视', say: '凑过去看看', open: '打开电视' },
  gametable: { title: '游戏桌', bar: '牌桌', say: '凑过去入座', open: '摊开了牌桌' },
};

/**
 * 画面缩放：网上的页面多半是照着宽屏电脑排的，原样塞进这框里字小得看不清，
 * 所以出厂就先把画面缩到 70%（相当于「缩小 30%」），手动能在 40%~200% 之间调。
 */
export const ZOOM_DEFAULT = 0.7;
export const ZOOM_MIN = 0.4;
export const ZOOM_MAX = 2;

/** 面板的位置 / 大小 / 缩放记在本机：{ tv: {x,y,w,h,zoom}, gametable: {...} } */
const VIEW_KEY = 'dtj_tv_view';
const viewMem = new Map();

export function clampZoom(z) {
  const n = Number(z);
  if (!isFinite(n) || !n) return ZOOM_DEFAULT;
  return Math.max(ZOOM_MIN, Math.min(ZOOM_MAX, n));
}

/** 没有 localStorage 的环境（冒烟测试）退化成内存：记不住也不会报错 */
function readViews() {
  try { return JSON.parse(localStorage.getItem(VIEW_KEY) || '{}') || {}; } catch { return viewMem.get('v') || {}; }
}
function writeViews(all) {
  viewMem.set('v', all);
  try { localStorage.setItem(VIEW_KEY, JSON.stringify(all)); } catch { /* 没处记就算了 */ }
}

/** 把用户粘贴的地址规范成「可嵌入」的地址 */
export function normalizeUrl(raw) {
  const s = String(raw || '').trim();
  if (!s) return null;

  let m = s.match(/bilibili\.com\/video\/(BV[0-9A-Za-z]{10})/i) || s.match(/^(BV[0-9A-Za-z]{10})$/i);
  if (m) return { kind: 'bilibili', url: `https://player.bilibili.com/player.html?bvid=${m[1]}&autoplay=1&high_quality=1&danmaku=0` };

  m = s.match(/bilibili\.com\/video\/av(\d+)/i);
  if (m) return { kind: 'bilibili', url: `https://player.bilibili.com/player.html?aid=${m[1]}&autoplay=1&high_quality=1&danmaku=0` };

  m = s.match(/(?:youtube\.com\/watch\?(?:[^#]*&)?v=|youtu\.be\/)([\w-]{6,})/i);
  if (m) return { kind: 'youtube', url: `https://www.youtube-nocookie.com/embed/${m[1]}?autoplay=1&rel=0` };

  if (/^https?:\/\//i.test(s)) return { kind: 'web', url: s };
  return null;
}

/** 给地址补上「从第几秒开始」，让后来的人直接对上进度 */
export function withSeek(url, seconds) {
  const s = Math.max(0, Math.floor(seconds || 0));
  if (!s || !url) return url;
  const sep = url.includes('?') ? '&' : '?';
  if (url.includes('player.bilibili.com')) return `${url}${sep}t=${s}`;
  if (url.includes('youtube')) return `${url}${sep}start=${s}`;
  return url;
}

/**
 * 这类地址跳不跳得了进度：B 站与油管认 t / start，
 * 普通网页与直播只能整块重新加载——这种情况得跟人说清楚，别让人白点。
 */
export function canSeek(url) {
  const u = String(url || '');
  return u.includes('player.bilibili.com') || u.includes('youtube');
}

/** 秒 -> 「分:秒」 */
export function fmtSec(sec) {
  const s = Math.max(0, Math.floor(sec || 0));
  return `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;
}

/** 「3:20」或「200」（纯秒）-> 秒 */
export function parseSeek(text) {
  const s = String(text || '').trim();
  if (!s) return null;
  if (/^\d+$/.test(s)) return Number(s);
  const m = s.match(/^(\d+):([0-5]?\d)$/);
  return m ? Number(m[1]) * 60 + Number(m[2]) : null;
}

export class TVPanel {
  /**
   * @param {import('../core/engine.js').Engine} engine
   * @param {import('./net.js').NetProxy} net
   * @param {{kind?: 'tv'|'gametable', el?: HTMLElement}} [opts]
   */
  constructor(engine, net, opts = {}) {
    this.engine = engine;
    this.net = net;
    this.kind = STAGE_TYPES.includes(opts.kind) ? opts.kind : 'tv';
    this.el = opts.el || document.getElementById(this.kind);   // 面板 id 与器物同名：tv / gametable
    this.input = this.q('.tv-url');
    this.frame = this.q('.tv-frame');
    this.info = this.q('.tv-info');
    this.titleEl = this.el ? this.el.querySelector('h3') : null;
    this.prop = null;
    this.state = { url: '', startedAt: 0, by: '' };
    this._askTimer = null;    // 问过屋里之后等人的那一小会儿
    this._ackTimer = null;    // 画面发出去之后等回话的那一小会儿
    this._acked = false;      // 这一趟有没有人接
    // 上次挪过的窗口位置与调过的画面缩放（电视归电视、牌桌归牌桌）
    const saved = readViews()[this.kind] || null;
    this.view = saved;
    this.zoom = clampZoom(saved && saved.zoom);

    if (!this.el) return;      // 没有 DOM（冒烟测试）也能跑收发逻辑
    this.bind();
  }

  /** 只在本块面板里找控件：两块面板的按钮各归各的，不会互相顶掉 */
  q(sel) { return this.el ? this.el.querySelector(sel) : null; }

  /** 面板上的叫法：电视 / 牌桌 */
  text() { return { kind: this.kind, ...STAGE_TEXT[this.kind] }; }

  bind() {
    const go = this.q('.tv-go');
    const syncBtn = this.q('.tv-sync');
    const miniBtn = this.q('.tv-mini');
    const fsBtn = this.q('.tv-fs');
    const closeBtn = this.el.querySelector('[data-close]');

    if (go) go.onclick = () => this.changeFromInput();
    if (this.input) {
      this.input.onkeydown = (e) => { if (e.key === 'Enter') this.changeFromInput(); };
    }
    if (syncBtn) syncBtn.onclick = () => this.syncNow();
    const seekGo = this.q('.tv-seekgo');
    const seekIn = this.q('.tv-seek');
    const doSeek = () => {
      const s = this.readSeekInput();
      if (s == null) return this.toast('在旁边填个时间，比如 3:20 或 200（秒）。', 'warn');
      this.seekTo(s);
    };
    if (seekGo) seekGo.onclick = doSeek;
    if (seekIn) seekIn.onkeydown = (e) => { if (e.key === 'Enter') doSeek(); };
    if (miniBtn) miniBtn.onclick = () => this.toggleMini();
    if (fsBtn) fsBtn.onclick = () => this.toggleFullscreen();
    if (closeBtn) closeBtn.onclick = () => this.close();

    // 系统偷偷退出全屏（Esc、切后台）也要把按钮文案收回来
    const fsChanged = () => this.syncFsBtn();
    document.addEventListener('fullscreenchange', fsChanged);
    document.addEventListener('webkitfullscreenchange', fsChanged);

    // 挪地方：小窗拖控制条，摊开时拖标题；右下角那个把手是改大小的
    const bar = this.el.querySelector('.tv-bar');
    if (bar) this.enableMove(bar);
    if (this.titleEl) this.enableMove(this.titleEl);
    this.enableResize(this.q('.tv-grip'));
    this.bindZoom();
    this.applyZoom();
    // 窗口缩放后：小窗别跑出画面，全屏铺满的尺寸也要跟着重算
    window.addEventListener('resize', () => {
      if (this.isMini()) this.clampIntoView();
      if (this.el && this.el.classList.contains('tv-fullscreen')) this.syncFullscreenBox();
    });

    // 只认自己这件家具：电视的开电视，牌桌的摊牌桌
    this.engine.events.on(EV.TV_OPEN, ({ prop }) => this.open(prop));
  }

  /* ------------------ 画面缩放、窗口大小与位置 ------------------ */

  bindZoom() {
    const ctl = this.q('.tv-zoomctl');
    if (!ctl) return;
    ctl.querySelectorAll('[data-tv-zoom]').forEach((btn) => {
      const m = btn.getAttribute('data-tv-zoom');
      btn.onclick = () => {
        if (m === '0') this.resetView();
        else if (m === '-') this.setZoom(this.zoom - 0.1);
        else if (m === '+') this.setZoom(this.zoom + 0.1);
      };
    });
  }

  /** 画面缩放到百分之多少；出厂 70%（也就是「先缩掉 30%」） */
  setZoom(z, { quiet = false } = {}) {
    this.zoom = clampZoom(z);
    this.applyZoom();
    this.persistView();
    if (!quiet) this.toast(`画面调到 ${Math.round(this.zoom * 100)}%。`, 'info');
  }

  /** 缩放交给 CSS 变量：iframe 自己放大再缩回来，新开的台也自动用同一个比例 */
  applyZoom() {
    if (!this.el) return;
    this.el.style.setProperty('--tv-zoom', String(this.zoom));
    const label = this.q('.tv-zoom');
    if (label) label.textContent = `${Math.round(this.zoom * 100)}%`;
  }

  /** 开门时把上回的位置、大小、缩放摆回来 */
  applyView() {
    if (!this.el) { return; }
    this.applyZoom();
    const v = this.view;
    if (!v) return;
    if (v.w) {
      this.el.style.width = `${v.w}px`;
      this.el.style.maxWidth = 'none';
      this.el.style.maxHeight = 'none';
    }
    if (v.h && this.frame) {
      this.frame.style.height = `${v.h}px`;
      this.frame.style.aspectRatio = 'auto';
      this.frame.style.maxHeight = 'none';
    }
    if (v.x != null && v.y != null) {
      this.el.style.left = `${v.x}px`;
      this.el.style.top = `${v.y}px`;
      this.el.style.right = 'auto';
      this.el.style.bottom = 'auto';
      this.el.style.transform = 'none';
    }
  }

  persistView(patch = null) {
    if (!this.el) return;
    this.view = { ...(this.view || {}), zoom: this.zoom, ...(patch || {}) };
    const all = readViews();
    all[this.kind] = this.view;
    writeViews(all);
  }

  /** 大小、位置、缩放全还原，重新交给 CSS 自己排 */
  resetView() {
    if (!this.el) return;
    this.el.style.width = '';
    this.el.style.maxWidth = '';
    this.el.style.maxHeight = '';
    this.el.style.left = '';
    this.el.style.top = '';
    this.el.style.right = '';
    this.el.style.bottom = '';
    this.el.style.transform = '';
    if (this.frame) {
      this.frame.style.height = '';
      this.frame.style.aspectRatio = '';
      this.frame.style.maxHeight = '';
    }
    const all = readViews();
    delete all[this.kind];
    writeViews(all);
    this.view = null;
    this.setZoom(ZOOM_DEFAULT, { quiet: true });
    this.toast('大小、位置、缩放都还原了。', 'info');
  }

  /* ------------------------------ 小窗 ------------------------------ */

  isMini() { return !!this.el && this.el.classList.contains('tv-mini'); }

  /** 缩到角上 / 放回原位。只换样式，不动 iframe，声音和进度不会断。 */
  toggleMini() { this.setMini(!this.isMini()); }

  /** 只切小窗样式与按钮字，不弹提示（全屏时内部用） */
  _setMiniClass(on) {
    if (!this.el) return;
    this.el.classList.toggle('tv-mini', on);
    const btn = this.q('.tv-mini');
    if (btn) btn.textContent = on ? '还原' : '小窗';
  }

  setMini(on) {
    if (!this.el) return;
    const was = this.isMini();
    this._setMiniClass(on);
    // 两种版式各自该待的地方由 CSS 排（小窗贴角、摊开居中），
    // 上一次拖出来的行内位置都清掉，切回去时不会粘在老地方
    this.el.style.left = '';
    this.el.style.top = '';
    this.el.style.right = '';
    this.el.style.bottom = '';
    this.el.style.transform = '';
    if (on) {
      this.clampIntoView();
    } else if (this.view) {
      // 还原就别再记着那个角了，下次开门还是居中待着
      this.persistView({ x: undefined, y: undefined });
    }
    const t = this.text();
    this.el.style.setProperty('--tv-mini-label', `'${t.bar}'`);   // 小窗顶上那条的名字
    if (was !== on) {
      this.toast(on ? `挂在角上了，拖着「${t.bar}」那一条能挪位置，照走不误。` : '放回原位。', 'info');
    }
  }

  /** #ui 整块是带着缩放的，这里把它算出来，好把手指的移动换算回面板自己的坐标 */
  uiScale() {
    if (!this.el) return 1;
    const p = this.el.offsetParent || this.el.parentElement;
    if (!p) return 1;
    const rect = p.getBoundingClientRect();
    const s = p.offsetWidth ? rect.width / p.offsetWidth : 1;
    return isFinite(s) && s ? s : 1;
  }

  /** 面板所在那一层的屏幕位置（做坐标换算用） */
  hostRect() {
    if (!this.el) return { left: 0, top: 0 };
    const p = this.el.offsetParent || this.el.parentElement;
    return p ? p.getBoundingClientRect() : { left: 0, top: 0 };
  }

  /** 拖着挪位置 */
  enableMove(handle) { this.enableDrag(handle, 'move'); }

  /** 拖着改大小（右下角那个把手） */
  enableResize(handle) { this.enableDrag(handle, 'resize'); }

  enableDrag(handle, mode = 'move') {
    if (!handle) return;
    handle.addEventListener('pointerdown', (ev) => {
      if (!this.el) return;
      if (this.el.classList.contains('tv-fullscreen')) return;   // 全屏时不挪
      if (ev.target.closest('button, input')) return;            // 按钮照常按
      this.startDrag(ev, mode);
      ev.preventDefault();
    });
  }

  /**
   * 一次拖动：mode 是 'move'（挪位置）或 'resize'（改大小）。
   * 只改 CSS，不碰 iframe 的节点，里面正在放的东西不会断。
   */
  startDrag(ev, mode) {
    const el = this.el;
    if (!el) return;
    const s0 = this.uiScale();
    const host = this.hostRect();
    const rect0 = el.getBoundingClientRect();
    const f0 = this.frame ? this.frame.getBoundingClientRect() : null;
    const base = {
      x: (rect0.left - host.left) / s0,
      y: (rect0.top - host.top) / s0,
      w: rect0.width / s0,
      fh: f0 ? f0.height / s0 : 0,
    };
    const p0 = { x: ev.clientX, y: ev.clientY };

    const onMove = (e) => {
      const s = this.uiScale() || 1;
      const dx = (e.clientX - p0.x) / s, dy = (e.clientY - p0.y) / s;
      if (mode === 'resize') {
        // 面板 CSS 上有「不许顶破画面」的上限，手动改大小时把这层兜底摘掉，
        // 剩下的只是不许超出屏幕
        const w = Math.max(220, Math.min(base.w + dx, (window.innerWidth - 20) / s));
        const h = Math.max(120, Math.min(base.fh + dy, (window.innerHeight - 40) / s));
        el.style.width = `${w}px`;
        el.style.maxWidth = 'none';
        el.style.maxHeight = 'none';
        if (this.frame) {
          // 手定过高度就别再跟着 16:9 走，照这个人定的样子
          this.frame.style.height = `${h}px`;
          this.frame.style.aspectRatio = 'auto';
          this.frame.style.maxHeight = 'none';
        }
        this._drag = { w, h };
        return;
      }
      // 挪位置：拖哪儿都行，就是不许拖出画面
      const minX = (2 - host.left) / s, maxX = (window.innerWidth - 2 - rect0.width - host.left) / s;
      const minY = (2 - host.top) / s, maxY = (window.innerHeight - 2 - rect0.height - host.top) / s;
      const x = Math.max(minX, Math.min(maxX, base.x + dx));
      const y = Math.max(minY, Math.min(maxY, base.y + dy));
      el.style.left = `${x}px`;
      el.style.top = `${y}px`;
      el.style.right = 'auto';
      el.style.bottom = 'auto';
      el.style.transform = 'none';        // 一直在中间待着的那副拽不动的劲儿松开
      this._drag = { x, y };
    };
    const onUp = () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
      if (this._drag) { this.persistView(this._drag); this._drag = null; }
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
  }

  /** 画面变了（转屏、缩放）就把面板拉回可见范围 */
  clampIntoView() {
    if (!this.el) return;
    if (!this.el.style.left) return;                    // 没挪过就交给 CSS 的默认位置
    const s = this.uiScale() || 1;
    const host = this.hostRect();
    const rect = this.el.getBoundingClientRect();
    const x = Math.max((2 - host.left) / s, Math.min((window.innerWidth - 2 - rect.width - host.left) / s, (rect.left - host.left) / s));
    const y = Math.max((2 - host.top) / s, Math.min((window.innerHeight - 2 - rect.height - host.top) / s, (rect.top - host.top) / s));
    this.el.style.left = `${x}px`;
    this.el.style.top = `${y}px`;
  }

  /* ------------------------------ 全屏 ------------------------------ */

  iframeEl() { return this.frame ? this.frame.querySelector('iframe') : null; }

  /** 浏览器原生全屏（iframe 自己铺满屏）或者页面内铺满，都算全屏 */
  isFullscreen() {
    if (!this.el) return false;
    if (this.el.classList.contains('tv-fullscreen')) return true;
    const fs = document.fullscreenElement || document.webkitCurrentFullScreenElement;
    if (!fs || !this.frame) return false;
    return fs === this.frame || fs === this.iframeEl() || this.frame.contains(fs);
  }

  /**
   * 全屏 / 退出。
   * 先请浏览器把 iframe 整块铺到屏幕上（这样才叫真全屏）；它不肯（iPhone 上的 Safari
   * 只允许视频元素全屏）就退一步：加 tv-fullscreen，在这一页里铺满，看得一样大。
   */
  async toggleFullscreen() {
    if (!this.el) return;
    if (this.isFullscreen()) return this.exitFullscreen();
    const target = this.iframeEl();
    if (!target) return this.toast('还没开台，先在上面贴个网址。', 'warn');

    // 小窗版式会把标题、输入框藏起来，铺满时得先收掉，退出全屏再还原
    this._wasMini = this.isMini();
    this._setMiniClass(false);

    const req = target.requestFullscreen || target.webkitRequestFullscreen;
    let ok = false;
    if (req) {
      try {
        await req.call(target);
        await new Promise((r) => setTimeout(r, 150));
        ok = this.isFullscreen();
      } catch (_) { ok = false; }          // 浏览器不给，走下面的兜底
    }
    if (!ok) {
      this.syncFullscreenBox();
      this.el.classList.add('tv-fullscreen');
      if (req) this.toast('浏览器不肯给这块画面全屏，改成在这一页里铺满。', 'warn');
    }
    this.syncFsBtn();
  }

  /**
   * 铺满用的尺寸：#ui 整块是被缩放过的（scale ≠ 1），这个面板又固定在它里面，
   * 所以得把屏幕大小换算回 #ui 自己的坐标系，铺出来才是真的一整屏。
   */
  syncFullscreenBox() {
    if (!this.el) return;
    const ui = this.el.offsetParent || this.el.parentElement;
    if (!ui) return;
    const rect = ui.getBoundingClientRect();
    const scale = ui.offsetWidth ? rect.width / ui.offsetWidth : 1;
    if (!scale || !isFinite(scale)) return;
    this.el.style.setProperty('--tv-fs-x', `${-rect.left / scale}px`);
    this.el.style.setProperty('--tv-fs-y', `${-rect.top / scale}px`);
    this.el.style.setProperty('--tv-fs-w', `${window.innerWidth / scale}px`);
    this.el.style.setProperty('--tv-fs-h', `${window.innerHeight / scale}px`);
  }

  exitFullscreen() {
    if (!this.el) return;
    this.el.classList.remove('tv-fullscreen');
    const fs = document.fullscreenElement || document.webkitCurrentFullScreenElement;
    if (fs) {
      const done = document.exitFullscreen || document.webkitExitFullscreen;
      if (done) { try { done.call(document); } catch (_) { /* 忽略 */ } }
    }
    if (this._wasMini) {          // 从小窗变的全屏，还给它小窗
      this._wasMini = false;
      this._setMiniClass(true);
      this.clampIntoView();
    }
    this.syncFsBtn();
  }

  syncFsBtn() {
    const btn = this.q('.tv-fs');
    if (btn) btn.textContent = this.isFullscreen() ? '退出' : '全屏';
  }

  toast(text, kind = 'info') { this.engine.events.emit(EV.TOAST, { text, kind }); }

  /**
   * 屋里这台（或这张桌子）眼下开着什么：面板没开过时实例里的 state 是空的，
   * 可频道是记在器物状态里的——换过台的人就算刷新了页面，也该答得出一句。
   */
  propState() {
    const list = this.engine.scene && this.engine.scene.props;
    const prop = list ? list.find((p) => p.type === this.kind) : null;
    return (prop && prop.state) || null;
  }

  /** 屋里眼下有没有别人（问联机那头数人头；摸不清的时候就当有，别瞎报「没人」） */
  hasCompany() {
    const coop = this.engine.coop;
    if (!coop || typeof coop.count !== 'function') return true;
    return coop.count() > 1;
  }

  changeFromInput() {
    const raw = this.input ? this.input.value : '';
    const n = normalizeUrl(raw);
    if (!n) return this.toast(`这个地址没法嵌进${this.text().title}里，试试 B 站或视频播放页。`, 'warn');
    this.change(n.url, { broadcast: true });
  }

  /** 换台；broadcast=true 时同步给房间里的其他人（带上是哪件家具，别串到另一块去） */
  change(url, { broadcast = false, by = '', startedAt = 0 } = {}) {
    this.state = { url, startedAt: startedAt || this.net.now(), by: by || '我' };
    this.render();
    this.saveToProp();
    if (broadcast && this.net.open) {
      this.net.send('tv', { url, startedAt: this.state.startedAt, by: this.net.selfId, kind: this.kind }, true);
    }
    this.toast(broadcast ? '已换台，房间里的人同步了。' : '频道已更新。', 'good');
  }

  /**
   * 点「对齐」：屋里的人看同一个画面。
   * 自己开着台 —— 连开播时刻一起播出去，各人按 (现在 - 开播) 跳到同一处；
   * 自己没开台 —— 问屋里一句，谁开着谁答；没联网就直说，别装作已经对齐。
   */
  syncNow() {
    const t = this.text();
    if (!this.net || !this.net.open) {
      this.render(true);
      this.toast(`还没连上（先在联机里进个频道），这台${t.title}眼下只你自己看得见。`, 'warn');
      return;
    }
    if (!this.state.url) {
      // 自己面板上没台，可屋里这台可能正播着（记在器物状态里）：先接过来，省得白问一句
      const saved = this.propState();
      if (saved && saved.url) {
        this.state = { url: saved.url, startedAt: saved.startedAt || this.net.now(), by: saved.by || '' };
      }
    }
    if (!this.state.url) {
      this.net.send('tv:ask', { kind: this.kind });
      clearTimeout(this._askTimer);
      // 等一小会儿没人答，就说明屋里没人开着，别让人干等着
      this._askTimer = setTimeout(() => {
        this._askTimer = null;
        if (!this.state.url) this.toast(`屋里没人开着${t.title}，你先在上面贴个网址。`, 'warn');
      }, 1500);
      this.toast(`问了屋里一句：${t.title}现在开着什么。`, 'info');
      return;
    }
    // 关键：带上「我这儿现在播到第几秒」与 sync 标记。
    // 对面照着这一秒直接跳，不用按自己的钟去算 —— 谁卡过、暂停过、晚进来过都能掰回来。
    const seek = Math.floor(Math.max(0, (this.net.now() - this.state.startedAt) / 1000));
    this.net.send('tv', {
      url: this.state.url, startedAt: this.state.startedAt, by: this.net.selfId, kind: this.kind,
      seek, sync: 1,
    }, true);
    this.render(true, seek);
    if (!canSeek(this.state.url)) {
      this.toast(`${t.title}里这块跳不了进度（只有 B 站、油管认「从第几秒开始」），屋里的人会一起重新加载，各人自己按播放键。`, 'warn');
      return;
    }
    if (!this.hasCompany()) {
      this.toast(`画面发出去了，可这屋里眼下没别人（先在联机里进同一个频道）——这台${t.title}暂时只有你自己看。`, 'warn');
      return;
    }
    this.toast(`已把${t.title}的画面发给屋里的人，进度也一并对齐。`, 'good');
    // 等一句回话：有人接就报个平安，没人接就直说，别让人对着按钮干瞪眼
    this._acked = false;
    clearTimeout(this._ackTimer);
    this._ackTimer = setTimeout(() => {
      this._ackTimer = null;
      if (this._acked) this.toast(`屋里的人接上了${t.title}的画面。`, 'good');
      else this.toast(`画面发出去了，可没人回话——对面多半没开这台${t.title}，或跟你不在同一个频道。`, 'warn');
    }, 1500);
  }

  /** 面板上「分:秒」那一栏填了什么（也认纯秒） */
  readSeekInput() {
    const el = this.q('.tv-seek');
    return el ? parseSeek(el.value) : null;
  }

  /**
   * 跳到第几秒，并让屋里的人一起跳过去。
   * 按开播时刻自动推算难免有出入（谁卡过、暂停过、晚进来过），这一下是手动掰回来的。
   */
  seekTo(sec) {
    const t = this.text();
    if (!this.state.url) return this.toast(`还没开台，先在上面贴个网址。`, 'warn');
    const s = Math.max(0, Math.floor(Number(sec) || 0));
    this.state = { ...this.state, startedAt: this.net.now() - s * 1000 };
    this.render(true, s);
    this.saveToProp();
    if (this.net.open) {
      this.net.send('tv', {
        url: this.state.url, startedAt: this.state.startedAt, by: this.net.selfId, kind: this.kind,
        seek: s, sync: 1,
      }, true);
      const tail = canSeek(this.state.url) ? '' : '（这块画面跳不了进度，屋里只会一起重新加载）';
      this.toast(`屋里一起跳到 ${fmtSec(s)}${tail}。`, 'good');
    } else {
      this.toast(`跳到 ${fmtSec(s)}（没联网，只有这台${t.title}动了）。`, 'info');
    }
  }

  /** 收到网络上的频道 / 询问；不是自己这件的消息一律不接 */
  onNet(msg) {
    if (!msg || msg.from === this.net.selfId) return;
    // 有人刚进屋：把「我这件家具」正在播的告诉他（对面是哪件由 kind 决定）
    if (msg.t === 'tv:ask') {
      if (msg.kind && STAGE_TYPES.includes(msg.kind) && msg.kind !== this.kind) return;   // 问的是电视，牌桌不搭腔
      if (!this.state.url) {
        // 我这边的面板没开过，实例里是空的：去器物状态里把屋里这台正播的翻出来答他
        const saved = this.propState();
        if (saved && saved.url) {
          this.state = { url: saved.url, startedAt: saved.startedAt || this.net.now(), by: saved.by || '' };
        }
      }
      if (this.state.url) {
        // 答的时候顺带说一句「我这儿播到第几秒」，刚坐下的人照这个跳，不用按自己的钟瞎猜
        const elapsed = Math.max(0, (this.net.now() - this.state.startedAt) / 1000);
        this.net.send('tv', {
          url: this.state.url, startedAt: this.state.startedAt, by: this.state.by || '', kind: this.kind,
          seek: Math.floor(elapsed),
        }, true);
      }
      return;
    }
    // 有人接上了我发的画面：回一句，好让那头知道没白发
    if (msg.t === 'tv:ack') {
      if (msg.kind && STAGE_TYPES.includes(msg.kind) && msg.kind !== this.kind) return;
      this._acked = true;
      return;
    }
    if (msg.t !== 'tv') return;
    // 老版本消息没有 kind，按电视处理，免得旧窗口收不到
    const kind = STAGE_TYPES.includes(msg.kind) ? msg.kind : 'tv';
    if (kind !== this.kind) return;    // 别人在换电视，不关牌桌的事
    if (this._askTimer) { clearTimeout(this._askTimer); this._askTimer = null; }   // 有人答了，别再报"屋里没人开着"

    const next = { url: msg.url || '', startedAt: msg.startedAt || this.net.now(), by: msg.by || '访客' };
    const sameUrl = !!next.url && next.url === this.state.url;
    const seek = (msg.seek == null || !isFinite(Number(msg.seek))) ? null : Number(msg.seek);
    const mine = sameUrl ? Math.max(0, (this.net.now() - this.state.startedAt) / 1000) : null;
    // 什么时候必须重载画面：换了台、对面点了「同步」（无条件）、或两边该播到的位置差过 2 秒
    let force = !sameUrl || !!msg.sync;
    if (!force && seek != null && mine != null && Math.abs(seek - mine) > 2) force = true;
    // 跳过去之后把开播时刻按「现在 - 那一秒」重算，后面接着往下走才不会再错开
    if (force && seek != null) next.startedAt = this.net.now() - seek * 1000;
    this.state = next;
    this.saveToProp();
    this.net.send('tv:ack', { kind: this.kind, url: next.url });   // 回一句：收到了
    const t = this.text();
    if (this.isOpen()) {
      this.render(force, force ? seek : null);
      this.toast(!sameUrl ? '有人换了频道。'
        : (msg.sync ? `屋里把${t.title}的进度拉齐了，一起跳到 ${fmtSec(seek || 0)}。`
          : (force ? '进度跟上屋里了。' : '有人换了频道。')), 'info');
    } else {
      this.toast(`有人${t.open}，${t.say}？`, 'info');
    }
  }

  open(prop) {
    if (!this.el) return;
    if (prop && prop.type && prop.type !== this.kind) return;   // 不是我这件家具
    this.prop = prop || null;
    // 频道记在器物状态里，下次进屋还记得
    this.state = { url: '', startedAt: 0, by: '' };
    if (prop && prop.state && prop.state.url) {
      this.state = { url: prop.state.url, startedAt: prop.state.startedAt || this.net.now(), by: prop.state.by || '' };
    }
    const t = this.text();
    if (this.titleEl) this.titleEl.textContent = t.title;
    this.el.classList.remove('hidden');
    if (this.isMini()) this.el.style.setProperty('--tv-mini-label', `'${t.bar}'`);
    this.applyView();          // 上一次挪到哪儿、多大、缩多少，照原样摆回来
    this.render(true);
    this.syncFsBtn();
    // 刚坐下先问一句屋里播到哪儿了：照自己这台钟算难免差着，屋里有人答就跳到他那儿
    if (this.net.open && this.state.url) this.net.send('tv:ask', { kind: this.kind });
  }

  close() {
    if (!this.el) return;
    this.exitFullscreen();                       // 全屏时先收回来，别把画面留在屏上
    this.el.classList.add('hidden');
    if (this.frame) this.frame.innerHTML = '';   // 停掉声音与播放
  }

  isOpen() { return !!this.el && !this.el.classList.contains('hidden'); }

  /**
   * 渲染 iframe；force=true 时强制重载以对齐进度。
   * 给了 seek 就照这一秒跳（对面的表比自己的钟准），否则按开播时刻自己算。
   */
  render(force = false, seek = null) {
    if (!this.frame) return;
    const { url, startedAt, by } = this.state;
    if (!url) {
      const empty = this.kind === 'gametable'
        ? '在上面贴一个网址，四条凳子的人都能一块儿看。'
        : '在上方贴一个网址，屋里的人就能一起看。';
      this.frame.innerHTML = `<div class="tv-empty">${empty}</div>`;
      if (this.info) this.info.textContent = '还没开台';
      return;
    }
    const elapsed = seek != null ? Math.max(0, seek) : Math.max(0, (this.net.now() - startedAt) / 1000);
    const src = withSeek(url, elapsed);
    const cur = this.frame.querySelector('iframe');
    if (!force && cur && cur.dataset.src === url) {
      if (this.info) this.info.textContent = this.infoText(elapsed, by);
      return;
    }
    this.frame.innerHTML = '';
    const f = document.createElement('iframe');
    f.src = src;
    f.dataset.src = url;
    f.allow = 'autoplay; fullscreen; encrypted-media; picture-in-picture';
    f.referrerPolicy = 'no-referrer';
    f.setAttribute('sandbox', 'allow-scripts allow-same-origin allow-presentation allow-popups allow-forms');
    this.frame.appendChild(f);
    if (this.info) this.info.textContent = this.infoText(elapsed, by);
  }

  infoText(sec, by) {
    const who = by ? `　由 ${by === '我' ? '你' : '访客'} 开台` : '';
    const hint = sec > 3 ? '　画面没动就点一下播放键' : '';   // 浏览器多半不许有声自动播放
    return `已开播 ${fmtSec(sec)}${who}${hint}`;
  }

  /** 频道记在这件器物自己的状态里：电视归电视，牌桌归牌桌 */
  saveToProp() {
    let prop = this.prop;
    if (!prop || prop.type !== this.kind) {
      prop = this.engine.scene
        ? this.engine.scene.props.find((p) => p.type === this.kind)
        : null;
    }
    if (!prop) return;
    prop.state = { ...(prop.state || {}), ...this.state };
    if (this.prop !== prop) this.prop = prop;
    const db = this.engine.db;
    if (db && prop.id) db.table('props').update(prop.id, { state: prop.state });
  }
}
